import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IncomingCallModal } from './components/IncomingCallModal';
import { useCallRequestsStore } from '@/stores/callRequests';
import { useToastStore } from '@/stores/toast';
import { useUsersStore } from '@/stores/users';
import { nameFormat } from '@/utils/valuesFormat';

/**
 * The user's ringing calls: a presenter sees a Calling toast for as long as
 * their call rings, and the user being called gets the Incoming Session modal
 * of the oldest call to them.
 * @returns The Incoming Session modal, when the user is being called.
 */
export const CallRequests = () => {
  const { t } = useTranslation();
  const callRequests = useCallRequestsStore((state) => state.callRequests);
  const checkRoleIds = useUsersStore((state) => state.checkRoleIds);
  const { setToast, removeToast } = useToastStore((state) => ({
    setToast: state.setToast,
    removeToast: state.removeToast,
  }));
  const callingToastIdsRef = useRef<string[]>([]);

  const incomingCall = callRequests.find(({ calleeUserInstitutionRoleId }) =>
    checkRoleIds([calleeUserInstitutionRoleId]),
  );

  useEffect(() => {
    const outgoingCalls = callRequests.filter(
      ({ callerUserInstitutionRoleId }) =>
        checkRoleIds([callerUserInstitutionRoleId]),
    );
    const toastIds = outgoingCalls.map(({ id, callee }) => {
      if (!callingToastIdsRef.current.includes(id)) {
        setToast(
          {
            title: t('call-request.calling', {
              name: nameFormat(callee?.firstName, callee?.lastName),
            }),
            variant: 'info',
          },
          { id, persistent: true },
        );
      }
      return id;
    });
    callingToastIdsRef.current
      .filter((toastId) => !toastIds.includes(toastId))
      .forEach(removeToast);
    callingToastIdsRef.current = toastIds;
  }, [callRequests, checkRoleIds, removeToast, setToast, t]);

  return incomingCall ? (
    <IncomingCallModal key={incomingCall.id} callRequest={incomingCall} />
  ) : null;
};
